import React, { useState, useEffect, useCallback } from 'react';
import API_BASE_URL from '../Config';
import { 
    Search, Calendar, Filter, RotateCcw, 
    ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
    User, CreditCard, CheckCircle, AlertCircle, Layers
} from 'lucide-react';

const LogDetalleProcesos = ({ codigo: initialCodigo }) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [procesos, setProcesos] = useState([]);
    const [selectedCodigo, setSelectedCodigo] = useState(initialCodigo || '');
    const [totalItems, setTotalItems] = useState(0);
    // ... rest of state
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [fechaInicio, setFechaInicio] = useState('');
    const [fechaFin, setFechaFin] = useState('');
    const [goToPage, setGoToPage] = useState('1');

    // Fetch process list for dropdown
    useEffect(() => {
        if (!initialCodigo) {
            const fetchProcesosList = async () => {
                try {
                    const token = localStorage.getItem('token');
                    const response = await fetch(`${API_BASE_URL}/procesos`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (response.ok) {
                        const data = await response.json();
                        setProcesos(data);
                    }
                } catch (error) {
                    console.error('Error fetching process list:', error);
                }
            };
            fetchProcesosList();
        }
    }, [initialCodigo]);

    const fetchDetalles = useCallback(async () => {
        if (!selectedCodigo) return;
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const start = fechaInicio ? `${fechaInicio}T00:00:00` : '';
            const end = fechaFin ? `${fechaFin}T23:59:59` : '';
            
            const url = new URL(`${API_BASE_URL}/procesos/detalles`);
            url.searchParams.append('codigo', selectedCodigo);
            url.searchParams.append('page', currentPage - 1);
            url.searchParams.append('size', rowsPerPage);
            if (searchTerm) url.searchParams.append('busqueda', searchTerm);
            if (start) url.searchParams.append('inicio', start);
            if (end) url.searchParams.append('fin', end);

            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (response.ok) {
                const data = await response.json();
                setItems(data.content || []);
                setTotalItems(data.totalElements || 0);
            }
        } catch (error) {
            console.error('Error fetching process details:', error);
        } finally {
            setLoading(false);
        }
    }, [selectedCodigo, currentPage, rowsPerPage, searchTerm, fechaInicio, fechaFin]);

    useEffect(() => {
        fetchDetalles();
    }, [fetchDetalles]);

    const totalPages = Math.ceil(totalItems / rowsPerPage);
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = Math.min(startIndex + rowsPerPage, totalItems);

    const handlePageChange = (newPage) => {
        const page = Math.max(1, Math.min(newPage, totalPages));
        setCurrentPage(page);
        setGoToPage(page.toString());
    };

    const handleGoToPage = (e) => {
        if (e.key === 'Enter') {
            const page = parseInt(goToPage);
            if (!isNaN(page)) handlePageChange(page);
        }
    };

    return (
        <div className="flex flex-col h-full bg-slate-50/50">
            {/* Toolbar / Filters */}
            <div className="p-4 bg-white border-b border-slate-200 flex flex-wrap items-center gap-4 shadow-sm">
                {!initialCodigo && (
                    <div className="flex items-center gap-2 mr-4 border-r border-slate-200 pr-4">
                        <Layers size={16} className="text-purple-600" />
                        <select 
                            className="bg-purple-50 border border-purple-100 text-purple-700 text-xs font-bold py-2 px-3 rounded-lg outline-none focus:ring-2 focus:ring-purple-500"
                            value={selectedCodigo}
                            onChange={(e) => { setSelectedCodigo(e.target.value); setCurrentPage(1); }}
                        >
                            <option value="">-- Seleccionar Proceso --</option>
                            {procesos.map(p => (
                                <option key={p.codigo} value={p.codigo}>{p.descripcion}</option>
                            ))}
                        </select>
                    </div>
                )}
                
                <div className="relative flex-1 min-w-[250px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                    <input 
                        type="text" 
                        placeholder="Buscar por socio o crédito..."
                        className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                        value={searchTerm}
                        onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                    />
                </div>

                <div className="flex items-center gap-2">
                    <div className="relative">
                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                        <input 
                            type="date" 
                            className="pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs outline-none focus:ring-2 focus:ring-blue-500"
                            value={fechaInicio}
                            onChange={(e) => { setFechaInicio(e.target.value); setCurrentPage(1); }}
                        />
                    </div>
                    <span className="text-slate-400 text-xs">al</span>
                    <div className="relative">
                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                        <input 
                            type="date" 
                            className="pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs outline-none focus:ring-2 focus:ring-blue-500"
                            value={fechaFin}
                            onChange={(e) => { setFechaFin(e.target.value); setCurrentPage(1); }}
                        />
                    </div>
                </div>

                <button 
                    onClick={() => { setSearchTerm(''); setFechaInicio(''); setFechaFin(''); setCurrentPage(1); }}
                    className="p-2 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Limpiar Filtros"
                >
                    <RotateCcw size={18} />
                </button>
            </div>

            {/* Table Area */}
            <div className="flex-1 overflow-auto p-4">
                <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-slate-50 border-b border-slate-200">
                            <tr>
                                <th className="px-4 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider w-40">Fecha / Hora</th>
                                <th className="px-4 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider">Socio / Referencia</th>
                                <th className="px-4 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider w-32">Estado</th>
                                <th className="px-4 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider">Descripción del Proceso</th>
                                <th className="px-4 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider w-32 text-right">Monto</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {loading ? (
                                <tr>
                                    <td colSpan="5" className="py-20 text-center">
                                        <div className="flex flex-col items-center gap-3">
                                            <RotateCcw className="animate-spin text-blue-500" size={32} />
                                            <span className="text-slate-400 font-medium">Consultando bitácora...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : items.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="py-20 text-center">
                                        <div className="flex flex-col items-center gap-2 text-slate-300">
                                            <Filter size={48} />
                                            <span className="font-bold text-lg">No se encontraron registros</span>
                                            <p className="text-sm">Intente ajustando los filtros de búsqueda o periodo.</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                items.map((item) => (
                                    <tr key={item.id} className="hover:bg-slate-50 transition-colors">
                                        <td className="px-4 py-3">
                                            <div className="text-[11px] font-bold text-slate-700">{new Date(item.fechaRegistro).toLocaleDateString()}</div>
                                            <div className="text-[10px] text-slate-400 font-mono">{new Date(item.fechaRegistro).toLocaleTimeString()}</div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-2">
                                                <div className="p-1.5 bg-blue-50 rounded text-blue-600">
                                                    <User size={14} />
                                                </div>
                                                <div>
                                                    <div className="text-[11px] font-bold text-slate-900">{item.socioNombre || 'N/A'}</div>
                                                    <div className="flex items-center gap-1 text-[10px] text-slate-500 font-mono">
                                                        <CreditCard size={10} /> {item.referenciaTipo}: #{item.referenciaId}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide border ${
                                                item.estado === 'PROCESADO' || item.estado === 'EXITO' 
                                                ? 'bg-emerald-50 text-emerald-700 border-emerald-100' 
                                                : 'bg-red-50 text-red-700 border-red-100'
                                            }`}>
                                                {item.estado === 'PROCESADO' || item.estado === 'EXITO' ? <CheckCircle size={10} /> : <AlertCircle size={10} />}
                                                {item.estado}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="text-[11px] text-slate-600 leading-relaxed italic">
                                                "{item.mensaje}"
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <div className="text-xs font-bold text-slate-900">
                                                {item.monto ? `Gs. ${new Intl.NumberFormat('es-PY').format(item.monto)}` : '-'}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination Footer */}
            <div className="p-4 bg-white border-t border-slate-200 flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                        <span className="text-[11px] font-medium text-slate-500">Ir a</span>
                        <input 
                            type="text" 
                            className="w-10 h-7 border border-slate-200 rounded text-center text-[11px] font-bold outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
                            value={goToPage}
                            onChange={(e) => setGoToPage(e.target.value)}
                            onKeyDown={handleGoToPage}
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-[11px] font-medium text-slate-500">Ver</span>
                        <select 
                            className="h-7 border border-slate-200 rounded text-[11px] font-bold px-1.5 outline-none focus:ring-2 focus:ring-blue-500 bg-white shadow-sm"
                            value={rowsPerPage}
                            onChange={(e) => {
                                setRowsPerPage(Number(e.target.value));
                                setCurrentPage(1);
                                setGoToPage('1');
                            }}
                        >
                            {[10, 20, 50, 100].map(val => (
                                <option key={val} value={val}>{val}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="flex items-center gap-1">
                    <button 
                        onClick={() => handlePageChange(1)}
                        disabled={currentPage === 1 || loading}
                        className={`p-1.5 rounded-lg hover:bg-slate-100 transition-colors ${currentPage === 1 ? 'text-slate-200' : 'text-slate-500'}`}
                    >
                        <ChevronsLeft className="w-4 h-4" />
                    </button>
                    <button 
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1 || loading}
                        className={`p-1.5 rounded-lg hover:bg-slate-100 transition-colors ${currentPage === 1 ? 'text-slate-200' : 'text-slate-500'}`}
                    >
                        <ChevronLeft className="w-4 h-4" />
                    </button>

                    <div className="flex items-center mx-2 gap-1">
                        {[...Array(totalPages)].map((_, i) => {
                            const p = i + 1;
                            if (totalPages > 5) {
                                if (p !== 1 && p !== totalPages && Math.abs(p - currentPage) > 1) {
                                    if (p === 2 || p === totalPages - 1) return <span key={p} className="text-slate-300">...</span>;
                                    return null;
                                }
                            }
                            return (
                                <button
                                    key={p}
                                    onClick={() => handlePageChange(p)}
                                    className={`w-7 h-7 rounded-lg text-[11px] font-bold transition-all ${currentPage === p ? 'bg-blue-600 text-white shadow-md' : 'text-slate-500 hover:bg-slate-100'}`}
                                >
                                    {p}
                                </button>
                            );
                        })}
                    </div>

                    <button 
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages || totalPages === 0 || loading}
                        className={`p-1.5 rounded-lg hover:bg-slate-100 transition-colors ${currentPage === totalPages || totalPages === 0 ? 'text-slate-200' : 'text-slate-500'}`}
                    >
                        <ChevronRight className="w-4 h-4" />
                    </button>
                    <button 
                        onClick={() => handlePageChange(totalPages)}
                        disabled={currentPage === totalPages || totalPages === 0 || loading}
                        className={`p-1.5 rounded-lg hover:bg-slate-100 transition-colors ${currentPage === totalPages || totalPages === 0 ? 'text-slate-200' : 'text-slate-500'}`}
                    >
                        <ChevronsRight className="w-4 h-4" />
                    </button>
                </div>

                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest bg-slate-50 px-2 py-1 rounded-md border border-slate-100">
                    [{totalItems > 0 ? startIndex + 1 : 0} A {endIndex} DE {totalItems}]
                </div>
            </div>
        </div>
    );
};

export default LogDetalleProcesos;
